(function (root, factory) {
  if (typeof module === 'object' && module.exports) { module.exports = factory(); }
  else { root.SorellaWeatherLogic = factory(); }
})(typeof self !== 'undefined' ? self : this, function () {
  const SEASON_LABELS = { Winter: 'cold winter days', Fall: 'crisp fall days', Spring: 'mild spring days', Summer: 'warm summer days' };
  const ESCAPE_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

  function mapTemperatureToSeason(celsius) {
    if (typeof celsius !== 'number' || Number.isNaN(celsius)) return null;
    if (celsius < 10) return 'Winter';
    if (celsius < 17) return 'Fall';
    if (celsius < 24) return 'Spring';
    return 'Summer';
  }

  function productKey(product) {
    return String(product.handle || product.title || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function daypartFrom(isDay, hour) {
    const hasApiDaypart = isDay !== null && isDay !== undefined && isDay !== '';
    if (hasApiDaypart && Number(isDay) === 0) return 'night';
    if (hasApiDaypart && Number(isDay) === 1) return 'day';
    const localHour = Number(hour);
    if (Number.isFinite(localHour)) return localHour >= 18 || localHour < 6 ? 'night' : 'day';
    return 'day';
  }

  function summerEditorialBoost(product, context) {
    if (context?.season !== 'Summer') return 0;
    const key = productKey(product);
    const daypart = daypartFrom(context?.isDay, context?.hour);
    const priorities = daypart === 'night'
      ? { 'tropy-kiss': 6, 'tan-line': 5.5 }
      : { 'fluffy-mallow': 6, 'silk-skin': 5.5, 'summer-skin': 5 };
    return priorities[key] || 0;
  }

  function scoreProduct(product, season, context = {}) {
    const profile = product.profile || {};
    const seasons = profile.seasons || [];
    const family = String(product.family || profile.family || '').toLowerCase();
    const intensity = String(product.intensity || profile.intensity || '').toLowerCase();
    let score = profile.bestseller ? 0.5 : 0;
    if (seasons.includes(season)) score += 4;
    else if (seasons.includes('All Year')) score += 3;
    const familyFit = {
      Winter: { earthy: 0.9, warm: 0.8, gourmand: 0.65, herbal: 0.25, floral: 0, flowery: 0, fruity: -0.1, fresh: -0.35 },
      Fall: { earthy: 0.75, warm: 0.75, gourmand: 0.6, herbal: 0.25, floral: 0, flowery: 0, fruity: 0.05, fresh: -0.2 },
      Spring: { floral: 0.9, flowery: 0.9, fruity: 0.8, fresh: 0.75, herbal: 0.4, gourmand: 0.1, warm: 0, earthy: -0.15 },
      Summer: { fresh: 0.9, fruity: 0.8, floral: 0.7, flowery: 0.7, herbal: 0.35, gourmand: 0.1, warm: -0.1, earthy: -0.3 },
    };
    score += familyFit[season]?.[family] || 0;
    if (season === 'Spring' || season === 'Summer') {
      if (intensity === 'soft') score += 0.35;
      if (intensity === 'statement') score -= 0.25;
    } else {
      if (intensity === 'statement') score += 0.3;
      if (intensity === 'soft') score -= 0.1;
    }
    score += summerEditorialBoost(product, Object.assign({}, context, { season }));
    return score;
  }

  function chooseVariant(product) {
    const variants = (product.variants || []).filter((variant) => variant.available);
    return variants.find((variant) => /(^|\s)30\s*ml($|\s)/i.test(`${variant.title} ${variant.format || ''}`))
      || variants.find((variant) => !/\b5\s*ml\b|tester/i.test(`${variant.title} ${variant.format || ''}`))
      || variants[0]
      || null;
  }

  function rankProducts(products, season, limit, context = {}) {
    const max = limit || 3;
    return products
      .filter((product) => product.available)
      .map((product) => {
        const variant = chooseVariant(product);
        if (!variant) return null;
        return Object.assign({}, product, { variant, score: scoreProduct(product, season, context) });
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score || a.title.localeCompare(b.title))
      .slice(0, max);
  }

  function reasonForWeather(season, temperature, city, context = {}) {
    const label = SEASON_LABELS[season] || 'today';
    const daypart = daypartFrom(context?.isDay, context?.hour);
    if (season === 'Summer' && daypart === 'night' && typeof temperature === 'number' && city) return `picked for a ${Math.round(temperature)}°C night in ${city}`;
    if (season === 'Summer' && daypart === 'night') return 'picked for a warm summer night';
    if (typeof temperature === 'number' && city) return `perfect for ${Math.round(temperature)}°C in ${city}`;
    if (typeof temperature === 'number') return `perfect for ${Math.round(temperature)}°C weather`;
    return `perfect for ${label}`;
  }

  function escapeHtml(value) {
    if (value == null) return '';
    return String(value).replace(/[&<>"']/g, (char) => ESCAPE_MAP[char]);
  }

  return { mapTemperatureToSeason, productKey, daypartFrom, summerEditorialBoost, scoreProduct, chooseVariant, rankProducts, reasonForWeather, escapeHtml };
});
